import { Component } from '@theme/component';

/**
 * Anmeldelser – alle: filter på stjerner og produkt, sortering og "Vis flere".
 *
 * Liquid renderer alle anmeldelser som <li> med data-stars, data-date, data-product og data-index.
 * Komponenten flytter rundt på dem og sætter hidden. Den henter intet fra serveren.
 * Markup og tekster ligger i blocks/locktown-review-wall.liquid.
 *
 * Masonry: Gitteret har rækker på 4px, og hvert kort fylder så mange rækker, som dets højde kræver. Kortene står
 * derfor i DOM-rækkefølge fra venstre mod højre, og "Vis flere" tilføjer nederst uden at flytte de viste kort.
 */

/**
 * @typedef {Object} LocktownReviewWallRefs
 * @property {HTMLUListElement} list
 * @property {HTMLElement} status
 * @property {HTMLButtonElement} reset
 * @property {HTMLSelectElement} sort
 * @property {HTMLElement} empty
 * @property {HTMLButtonElement} [more]
 */

/** @extends {Component<LocktownReviewWallRefs>} */
class LocktownReviewWall extends Component {
  requiredRefs = ['list', 'status', 'reset', 'sort', 'empty'];

  /** Valgt antal stjerner. 0 = alle. */
  #stars = 0;
  /** Valgt produkt-handle. Tom = alle. */
  #product = '';
  #shown = 0;

  /** Højden på én række i masonry-gitteret. Skal passe med grid-auto-rows i blockens CSS. */
  static ROW_HEIGHT = 4;

  /** Måler kortene og sætter, hvor mange rækker hvert kort fylder. */
  #resizeObserver = new ResizeObserver((entries) => {
    const gap = parseFloat(getComputedStyle(this.refs.list).columnGap) || 0;
    for (const { target, borderBoxSize } of entries) {
      const height = borderBoxSize?.[0]?.blockSize ?? /** @type {HTMLElement} */ (target).offsetHeight;
      // Skjulte kort måles til 0. De får en ny måling, når de vises igen.
      if (!height) continue;
      const span = Math.ceil((height + gap) / LocktownReviewWall.ROW_HEIGHT);
      /** @type {HTMLElement} */ (target).style.setProperty('--locktown-span', String(span));
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#shown = this.#pageSize;
    this.#render();
    for (const card of this.#cards) this.#resizeObserver.observe(card);
    this.refs.list.classList.add('locktown-review-wall__list--masonry');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  get #pageSize() {
    return Number(this.dataset.pageSize) || 12;
  }

  /** @returns {HTMLLIElement[]} */
  get #cards() {
    return /** @type {HTMLLIElement[]} */ ([...this.refs.list.children]);
  }

  /** @param {Event} event */
  filterStars(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const stars = Number(button.dataset.stars) || 0;
    this.#stars = this.#stars === stars ? 0 : stars;
    this.#shown = this.#pageSize;
    this.#render();
  }

  /** @param {Event} event */
  filterProduct(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    this.#product = button.dataset.product ?? '';
    this.#shown = this.#pageSize;
    this.#render();
  }

  sort() {
    this.#shown = this.#pageSize;
    this.#render();
  }

  reset() {
    this.#stars = 0;
    this.#product = '';
    this.#shown = this.#pageSize;
    this.#render();
  }

  showMore() {
    const start = this.#shown;
    this.#shown += this.#pageSize;
    this.#render();
    // Fokus på det første nye kort, så tastatur og skærmlæser fortsætter der.
    const firstNew = this.#cards.filter((card) => !card.hidden)[start];
    if (firstNew) {
      firstNew.tabIndex = -1;
      firstNew.focus({ preventScroll: true });
    }
  }

  /** @param {HTMLElement} card */
  #matches(card) {
    if (this.#stars && Number(card.dataset.stars) !== this.#stars) return false;
    if (this.#product && card.dataset.product !== this.#product) return false;
    return true;
  }

  /** @returns {(a: HTMLElement, b: HTMLElement) => number} */
  #comparator() {
    const byIndex = (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) =>
      Number(a.dataset.index) - Number(b.dataset.index);
    // Anmeldelser uden dato kommer sidst. ISO-datoer kan sammenlignes som tekst.
    const byDate = (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) => {
      const dateA = a.dataset.date ?? '';
      const dateB = b.dataset.date ?? '';
      if (dateA === dateB) return byIndex(a, b);
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA < dateB ? 1 : -1;
    };
    const byStars = (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) =>
      Number(a.dataset.stars) - Number(b.dataset.stars);

    switch (this.refs.sort.value) {
      case 'highest':
        return (a, b) => byStars(b, a) || byDate(a, b);
      case 'lowest':
        return (a, b) => byStars(a, b) || byDate(a, b);
      default:
        return byDate;
    }
  }

  #render() {
    const { list, status, reset, empty, more } = this.refs;

    const sorted = this.#cards.sort(this.#comparator());
    const matches = sorted.filter((card) => this.#matches(card));
    const visible = new Set(matches.slice(0, this.#shown));

    for (const card of sorted) card.hidden = !visible.has(card);
    // Kun flyt kortene, hvis rækkefølgen faktisk har ændret sig.
    if (sorted.some((card, index) => list.children[index] !== card)) list.append(...sorted);

    status.textContent = (this.dataset.statusText ?? '')
      .replace('[vist]', String(visible.size))
      .replace('[antal]', String(matches.length));
    reset.hidden = !this.#stars && !this.#product;
    empty.hidden = matches.length > 0;
    if (more) more.hidden = matches.length <= this.#shown;

    for (const button of this.querySelectorAll('[on\\:click="/filterStars"]')) {
      button.setAttribute('aria-pressed', String(Number(/** @type {HTMLElement} */ (button).dataset.stars) === this.#stars));
    }
    for (const button of this.querySelectorAll('[on\\:click="/filterProduct"]')) {
      button.setAttribute('aria-pressed', String((/** @type {HTMLElement} */ (button).dataset.product ?? '') === this.#product));
    }
  }
}

if (!customElements.get('locktown-review-wall-component')) {
  customElements.define('locktown-review-wall-component', LocktownReviewWall);
}
